import { ImageLinkProps } from '@awazevr/design-system.components.image-link'
import { LinkProps } from '@awazevr/design-system.components.link'
import { MegaMenuProps } from '@awazevr/design-system.compositions.mega-menu'
import {
  NavigationItemProps,
  NavigationItemType,
} from '@awazevr/design-system.compositions.responsive-header'

import {
  IImageLink,
  ILink,
  IMegaMenu,
  IMegaMenuLeftPane,
  IMegaMenuRightPane,
  IMenuItem,
} from '../../../@types/generated/contentful-global'

export const unwrapLink = (link: ILink): Omit<LinkProps, 'children'> => {
  return {
    title: link.fields?.displayText,
    href: link.fields?.href ?? '',
    openInNewTab: link.fields?.openInNew,
    rel: link.fields?.rel,
  }
}

export const unwrapImageLink = (value: IImageLink): ImageLinkProps => {
  return {
    title: value.fields?.text ?? '',
    link: {
      href: value.fields?.url ?? '',
    },
    image: value.fields?.image?.fields.file?.url
      ? {
          src: value.fields?.image?.fields.file.url,
          alt: value.fields?.image?.fields.description ?? '',
        }
      : {
          src: '',
          alt: '',
        },
  }
}

export const unwrapMegaMenuLeftSection = (
  leftPane: IMegaMenuLeftPane | undefined,
): MegaMenuProps['leftSection'] | undefined => {
  if (!leftPane?.fields) {
    return undefined
  }
  const { title, links, footerLinkText, footerLinkUrl, isMultiColumn } =
    leftPane.fields

  return {
    title,
    footerLink:
      footerLinkText && footerLinkUrl
        ? {
            title: footerLinkText,
            href: footerLinkUrl,
          }
        : undefined,
    links: links
      ?.map((link) => ({ link: unwrapLink(link) }))
      .filter((link) => link.link && link.link.title),
    isMultiColumns: isMultiColumn?.valueOf(),
  }
}

export const unwrapMegaMenuRightSection = (
  rightPane: IMegaMenuRightPane | undefined,
): MegaMenuProps['rightSection'] | undefined => {
  if (!rightPane?.fields) {
    return undefined
  }
  const { title, imageOrLink, footerLinkText, footerLinkUrl, showAsALinks } =
    rightPane.fields

  return {
    title,
    footerLink:
      footerLinkText && footerLinkUrl
        ? {
            title: footerLinkText,
            href: footerLinkUrl,
          }
        : undefined,
    isImageLinks: showAsALinks?.valueOf(),
    links: imageOrLink
      ?.map(unwrapImageLink)
      .filter((link) => link && link.title),
  }
}

export const unwrapMegaMenu = (megaMenu: IMegaMenu): MegaMenuProps => {
  const { id, displayText, leftPane, rightPane } = megaMenu.fields
  return {
    id: id || '',
    displayText: displayText || '',
    leftSection: leftPane && unwrapMegaMenuLeftSection(leftPane),
    rightSection: rightPane && unwrapMegaMenuRightSection(rightPane),
  }
}

export const unwrapMenuItem = (item: IMenuItem): NavigationItemProps => {
  const {
    type,
    link,
    highlight,
    title,
    icon,
    subMenu,
    megaMenu,
    enableMobileLoginIcon,
    displayFor,
    showUserEmail,
  } = item.fields

  return {
    id: link.fields.id || '',
    type,
    link: {
      displayText: link.fields.displayText || '',
      href: link.fields.href,
      id: link.fields.id || '',
      openInNew: link.fields.openInNew,
      rel: link.fields.rel,
    },
    highlight,
    title,
    icon,
    subMenu: subMenu && unwrapMenu(subMenu),
    megaMenu: megaMenu && unwrapMegaMenu(megaMenu),
    enableMobileLoginIcon,
    displayFor,
    showUserEmail,
  }
}

export const unwrapMenu = (
  menu: IMenuItem[] | undefined,
): NavigationItemType[] => (menu ? menu.map(unwrapMenuItem) : [])
